/**
 * Suspension temporaire de vente — bloc PDP « Temporairement indisponible »
 * + inscription à l'alerte retour en disponibilité.
 *
 * Le blocage de la vente est fait côté Shopify (inventoryPolicy = DENY posé par
 * sync_suspensions.py) : ce fichier ne gère que l'affichage et la collecte d'email.
 *
 * L'inscription part directement chez Klaviyo via la Client API (clé publique, aucun
 * secret exposé) sous la métrique « Back In Stock Requested ». Le `unique_id`
 * bis-<variant>-<email> fait la déduplication côté Klaviyo : un même client ne peut
 * pas s'inscrire deux fois pour la même référence.
 */
(function () {
  'use strict';

  var KLAVIYO_REVISION = '2024-10-15';
  var METRIC = 'Back In Stock Requested';
  var STORAGE_KEY = 'tcr:bis';

  /**
   * L'app embed Klaviyo charge static.klaviyo.com/onsite/js/<company_id>/klaviyo.js.
   * On lit l'id depuis ce script plutôt que de le figer dans le thème : si le compte
   * Klaviyo change, rien à redéployer côté thème.
   */
  function companyId() {
    var scripts = document.querySelectorAll('script[src*="klaviyo.com/onsite/js/"]');
    for (var i = 0; i < scripts.length; i++) {
      var match = scripts[i].src.match(/onsite\/js\/([A-Za-z0-9]+)\//);
      if (match) return match[1];
    }
    // Repli : compte Klaviyo TCR (XdyUxX). Utilisé seulement si l'app embed n'a pas
    // encore injecté son script au moment de l'envoi.
    return 'XdyUxX';
  }

  function alreadySubscribed(variantId) {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw).indexOf(String(variantId)) !== -1 : false;
    } catch (e) {
      return false;
    }
  }

  function rememberSubscription(variantId) {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      if (list.indexOf(String(variantId)) === -1) list.push(String(variantId));
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(-200)));
    } catch (e) {
      /* navigation privée / storage bloqué : on n'empêche pas l'inscription */
    }
  }

  function t(element, key, fallback) {
    var value = element.getAttribute('data-i18n-' + key);
    return value || fallback;
  }

  class ProductSuspension extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-suspension-form]');
      this.input = this.querySelector('input[type="email"]');
      this.message = this.querySelector('[data-suspension-message]');
      this.submitButton = this.querySelector('[type="submit"]');
      this.availability = this.readAvailability();

      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      this.onVariantChange = this.onVariantChange.bind(this);
      document.addEventListener('variant:changed', this.onVariantChange);

      this.syncState(this.getAttribute('data-variant-id'));
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this.onVariantChange);
    }

    readAvailability() {
      var node = this.querySelector('[data-suspension-variants]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return {};
      }
    }

    onVariantChange(event) {
      var variant = event && event.detail && event.detail.variant;
      if (!variant || !variant.id) return;
      this.setAttribute('data-variant-id', variant.id);
      // La disponibilité portée par l'événement fait foi ; la carte JSON n'est qu'un
      // repli pour le premier rendu.
      this.syncState(variant.id, variant.available);
    }

    /** Affiche le bloc uniquement quand la variante courante n'est pas achetable. */
    syncState(variantId, availableFromEvent) {
      var available = availableFromEvent;
      if (typeof available !== 'boolean') {
        available = this.availability[String(variantId)];
      }
      var blocked = available === false;
      this.classList.toggle('hidden', !blocked);
      this.relabelSubmitButtons(blocked);
      this.toggleContainerState(blocked);

      if (blocked && this.input) {
        this.resetMessage();
        if (alreadySubscribed(variantId)) {
          this.showMessage(
            t(this, 'already', 'Vous êtes déjà inscrit pour cette référence.'),
            'info'
          );
          this.input.value = '';
        }
      }
    }

    /**
     * Porte l'état « suspension effective » sur le conteneur d'infos produit, ce qui
     * masque le délai de livraison de l'app Dropship Supplier Manager (cf.
     * component-product-suspension.css). Rendu côté Liquid au chargement, reposé ici
     * au changement de variante : une variante peut rester achetable pendant que la
     * précédente ne l'est plus.
     */
    toggleContainerState(blocked) {
      var container = this.closest('.product__info-container');
      if (container) container.classList.toggle('product__info-container--suspended', blocked);
    }

    /**
     * `product-info.js` (et `sticky-atc-bar.js`) réécrivent le libellé du bouton avec
     * `FoxTheme.variantStrings.soldOut` à chaque changement de variante : le rendu
     * Liquid « Temporairement indisponible » serait donc écrasé par « Épuisé » dès le
     * premier changement. On repose le bon libellé après eux.
     */
    relabelSubmitButtons(blocked) {
      if (!blocked) return;
      var label = this.querySelector('.product-suspension__title');
      if (!label) return;
      var text = label.textContent.trim();
      var sectionId = this.getAttribute('data-section-id');
      var buttons = document.querySelectorAll(
        '#ProductSubmitButton-' + sectionId + ', sticky-atc-bar [type="submit"]'
      );
      for (var i = 0; i < buttons.length; i++) {
        this.enforceDisabled(buttons[i], text);
      }
    }

    /**
     * Garde-fou contre l'app Dropship Supplier Manager.
     *
     * Son bloc produit injecte un script qui, sur chaque mutation du DOM, réactive le
     * bouton d'achat et le réétiquette « Ajouter au panier » — c'est sa raison d'être :
     * vendre en backorder avec un délai. Il ne le fait que si la variante est en
     * `continue` (`if (data.p !== 'continue') return;`), donc pendant une suspension
     * (variante passée en `deny`) il reste inerte : les deux mécanismes s'accordent.
     *
     * On réaffirme quand même l'état désactivé au cas où sa charge `LTD_VARIANTS`
     * serait servie avant la bascule en `deny`. Sans ce filet, le client verrait un
     * bouton actif sur un produit que Shopify refuserait au panier.
     */
    enforceDisabled(button, text) {
      var apply = function () {
        if (!button.hasAttribute('disabled')) button.setAttribute('disabled', '');
        var span = button.querySelector('span');
        if (span && span.textContent.trim() !== text) span.textContent = text;
      };
      apply();
      if (button.dataset.suspensionGuarded === 'true') return;
      button.dataset.suspensionGuarded = 'true';
      // Observe uniquement ce bouton : pas de boucle avec l'app, qui sort en amont
      // sur `deny` et ne réagit donc pas à nos écritures.
      new MutationObserver(apply).observe(button, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        subtree: true,
      });
    }

    resetMessage() {
      if (!this.message) return;
      this.message.hidden = true;
      this.message.textContent = '';
      this.message.classList.remove('is-error', 'is-success', 'is-info');
    }

    showMessage(text, kind) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.classList.remove('is-error', 'is-success', 'is-info');
      this.message.classList.add('is-' + (kind || 'info'));
      this.message.hidden = false;
    }

    setBusy(busy) {
      if (!this.submitButton) return;
      this.submitButton.disabled = busy;
      this.submitButton.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    onSubmit(event) {
      event.preventDefault();
      var email = (this.input && this.input.value || '').trim();
      if (!email || email.indexOf('@') < 1 || email.lastIndexOf('.') < email.indexOf('@')) {
        this.showMessage(t(this, 'invalid', 'Merci de saisir une adresse email valide.'), 'error');
        return;
      }

      var variantId = this.getAttribute('data-variant-id');
      this.setBusy(true);
      this.resetMessage();

      // `image_url` renvoie une URL protocol-relative (//cdn…) : illisible dans un
      // email Klaviyo, on la rend absolue avant l'envoi.
      var image = this.getAttribute('data-product-image') || '';
      if (image.indexOf('//') === 0) image = 'https:' + image;

      var body = {
        data: {
          type: 'event',
          attributes: {
            unique_id: 'bis-' + variantId + '-' + email.toLowerCase(),
            properties: {
              variant_id: String(variantId),
              product_id: this.getAttribute('data-product-id'),
              handle: this.getAttribute('data-product-handle'),
              title: this.getAttribute('data-product-title'),
              url: this.getAttribute('data-product-url'),
              image: image,
              locale: this.getAttribute('data-locale') || 'fr',
              expected_return: this.getAttribute('data-return-date'),
            },
            metric: { data: { type: 'metric', attributes: { name: METRIC } } },
            // `lang` est la propriété profil sur laquelle branchent tous les flows
            // TCR (REF·, FID26·). On la renseigne ici pour qu'un contact inscrit à
            // l'alerte parte dans la bonne langue même s'il n'était pas connu.
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: { lang: this.getAttribute('data-locale') || 'fr' },
                },
              },
            },
          },
        },
      };

      var self = this;
      fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(companyId()), {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(body),
      })
        .then(function (response) {
          self.setBusy(false);
          if (response.ok || response.status === 202) {
            rememberSubscription(variantId);
            self.showMessage(
              t(self, 'success', 'Merci, nous vous préviendrons dès le retour en stock.'),
              'success'
            );
            if (self.input) self.input.value = '';
          } else {
            self.showMessage(
              t(self, 'error', 'Inscription impossible pour le moment, merci de réessayer.'),
              'error'
            );
          }
        })
        .catch(function () {
          self.setBusy(false);
          self.showMessage(
            t(self, 'error', 'Inscription impossible pour le moment, merci de réessayer.'),
            'error'
          );
        });
    }
  }

  if (!customElements.get('product-suspension')) {
    customElements.define('product-suspension', ProductSuspension);
  }
})();
